import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { productAPI, cartAPI } from '../services/api';
import { useTranslation } from 'react-i18next';
import Navigation from '../components/layout/Navigation';
import Footer from '../components/layout/Footer';
import { ShoppingCart, Sparkles, Check } from 'lucide-react';
import { SingleCardIcon, DoubleCardIcon } from '../components/CardIcons';

const Shop = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const category = searchParams.get('category');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await productAPI.getAll(category);
        setProducts(response.data);
      } catch (error) {
        console.error('Failed to fetch products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [category]);

  const addToCart = async (productId) => {
    setAddingToCart(productId);
    try {
      await cartAPI.add({ product_id: productId, quantity: 1 });
      alert(t('shop.added') || 'Added to cart!');
    } catch (error) {
      alert('Failed to add to cart');
    } finally {
      setAddingToCart(null);
    }
  };

  const categoryNames = {
    base: t('shop.categories.base'),
    elite: t('shop.categories.elite'),
    foil: t('shop.categories.foil'),
    custom: t('shop.categories.custom')
  };

  const categories = ['base', 'elite', 'foil', 'custom'];

  // Card placeholder images from MTG theme
  const cardPlaceholders = {
    base: 'https://images.unsplash.com/photo-1732944710507-feecdfb4f2c1?w=400',
    elite: 'https://images.unsplash.com/photo-1593814681464-eef5af2b0628?w=400',
    foil: 'https://images.unsplash.com/photo-1593814681110-cbf22baf6d79?w=400',
    custom: 'https://images.unsplash.com/photo-1513002433973-e0a181372d60?w=400',
    commander: 'https://images.unsplash.com/photo-1513002433973-e0a181372d60?w=400'
  };

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-brand-bg pt-24 px-6 pb-16">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div 
            className="mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-5xl md:text-6xl font-outfit font-light tracking-tight text-white mb-3">
              {category ? categoryNames[category] : t('shop.allProducts')}
            </h1>
            <p className="text-zinc-400 text-lg mb-8">
              {t('shop.subtitle') || 'Premium quality MTG proxy cards'}
            </p>
            
            {/* Category Filters */}
            <div className="flex flex-wrap gap-3">
              <a 
                href="/shop" 
                className={`px-6 py-3 rounded-xl font-semibold text-sm transition-all ${
                  !category 
                    ? 'bg-violet-600 text-white glow-violet shadow-lg shadow-violet-600/30' 
                    : 'bg-brand-surface border border-brand-border text-zinc-400 hover:text-white hover:border-violet-600 hover:bg-zinc-800'
                }`}
              >
                {t('shop.categories.all')}
              </a>
              {categories.map((cat) => (
                <a 
                  key={cat}
                  href={`/shop?category=${cat}`}
                  className={`px-6 py-3 rounded-xl font-semibold text-sm transition-all ${
                    category === cat 
                      ? 'bg-violet-600 text-white glow-violet shadow-lg shadow-violet-600/30' 
                      : 'bg-brand-surface border border-brand-border text-zinc-400 hover:text-white hover:border-violet-600 hover:bg-zinc-800'
                  }`}
                >
                  {categoryNames[cat]}
                </a>
              ))}
            </div>
          </motion.div>

          {/* Products Grid - More spacious */}
          {loading ? (
            <div className="text-center py-24">
              <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-violet-600 mx-auto mb-4"></div>
              <p className="text-zinc-400">Loading products...</p>
            </div>
          ) : (
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8"
              initial="hidden"
              animate="visible"
              variants={{
                visible: {
                  transition: {
                    staggerChildren: 0.1
                  }
                }
              }}
            >
              {products.map((product) => {
                const isSpecial = product.category === 'foil';
                const placeholderImg = cardPlaceholders[product.category] || cardPlaceholders.base;
                
                // Simplify product name - remove redundant pack info
                const simplifyName = (name) => {
                  // Remove "- Single", "- 50 Pack", "- 100 Pack", etc. and numbers
                  return name
                    .replace(/\s*-\s*(Single|10 Pack|50 Pack|100 Pack|Commander Deck)\s*/gi, ' ')
                    .replace(/\s+/g, ' ')
                    .trim();
                };
                
                const simplifiedName = simplifyName(product.name);
                
                // Determine quantity for overlay (x50, x100, etc.)
                const getQuantityBadge = () => {
                  const name = product.name.toLowerCase();
                  if (name.includes('single')) return 'x1';
                  if (name.includes('10 pack')) return 'x10';
                  if (name.includes('50 pack') || name.includes('50')) return 'x50';
                  if (name.includes('100 pack') || name.includes('100') || name.includes('commander')) return 'x100';
                  return null;
                };
                
                const quantityBadge = getQuantityBadge();
                
                // Determine print side
                const isPrintDoubleSided = product.name.toLowerCase().includes('double');
                const isPrintFrontOnly = product.name.toLowerCase().includes('front');
                
                return (
                  <motion.div
                    key={product.product_id}
                    variants={{
                      hidden: { opacity: 0, y: 30 },
                      visible: { opacity: 1, y: 0 }
                    }}
                    whileHover={{ y: -8, scale: 1.02 }}
                    transition={{ type: "spring", stiffness: 300 }}
                    className="group relative bg-brand-surface border border-brand-border rounded-2xl overflow-hidden hover:border-violet-600 hover:shadow-2xl hover:shadow-violet-600/20 transition-all"
                  >
                    {/* Product Image */}
                    <div className="relative h-56 overflow-hidden">
                      <img 
                        src={placeholderImg}
                        alt={simplifiedName}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      
                      {/* Foil effect overlay */}
                      {isSpecial && (
                        <div className="absolute inset-0 z-10 bg-gradient-to-br from-amber-400/30 via-yellow-300/20 to-amber-600/30 opacity-60 group-hover:opacity-80 transition-opacity animate-shimmer pointer-events-none"></div>
                      )}
                      
                      {/* Quantity Badge (x50, x100) */}
                      {quantityBadge && (
                        <div className="absolute top-4 left-4 z-20 bg-black/80 backdrop-blur-sm text-white px-4 py-2 rounded-lg text-base font-bold border border-white/20 shadow-lg">
                          {quantityBadge}
                        </div>
                      )}
                      
                      {/* Foil Badge */}
                      {isSpecial && (
                        <div className="absolute top-4 right-4 z-20 bg-gradient-to-r from-amber-600 to-amber-400 text-white px-4 py-2 rounded-lg text-xs font-bold flex items-center space-x-1.5 shadow-lg">
                          <Sparkles size={14} />
                          <span>FOIL</span>
                        </div>
                      )}
                    </div>

                    {/* Product Info */}
                    <div className="p-6">
                      {/* Title with Print Type Badge */}
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="text-lg font-outfit font-semibold text-white group-hover:text-violet-400 transition-colors leading-tight flex-1">
                          {simplifiedName}
                        </h3>
                        {/* Print side icon */}
                        {isPrintDoubleSided && (
                          <div className="flex-shrink-0 ml-2 bg-blue-600/20 text-blue-400 p-2 rounded-lg border border-blue-600/30" title="Double-Sided">
                            <DoubleCardIcon size={20} />
                          </div>
                        )}
                        {isPrintFrontOnly && (
                          <div className="flex-shrink-0 ml-2 bg-zinc-600/20 text-zinc-400 p-2 rounded-lg border border-zinc-600/30" title="Front Only">
                            <SingleCardIcon size={20} />
                          </div>
                        )}
                      </div>

                      {/* Price */}
                      <p className="text-3xl font-bold text-violet-600 mb-4">{product.price}€</p>

                      {/* Description */}
                      <p className="text-zinc-400 text-sm mb-5 line-clamp-2 leading-relaxed">
                        {product.description}
                      </p>
                      
                      {/* Features - Clean list */}
                      <ul className="space-y-2 mb-6">
                        {product.features.slice(0, 3).map((feature, idx) => (
                          <li key={idx} className="text-xs text-zinc-400 flex items-start">
                            <Check size={14} className="text-violet-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>

                      {/* Add to Cart Button */}
                      <button
                        onClick={() => addToCart(product.product_id)}
                        disabled={addingToCart === product.product_id}
                        className="w-full px-6 py-4 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl font-bold hover:from-violet-500 hover:to-purple-500 transition-all flex items-center justify-center space-x-2 disabled:opacity-50 shadow-lg shadow-violet-600/30"
                      >
                        <ShoppingCart size={18} />
                        <span>{addingToCart === product.product_id ? t('shop.adding') : t('shop.addToCart')}</span>
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Shop;
